import { useSyncExternalStore } from "react";
import { queryClient } from "./trpc.js";

/**
 * Обновление кабинета после выкладки. Service worker сразу встаёт на место старого (`skipWaiting` и `clientsClaim`
 * в `sw.ts`), а открытая вкладка продолжает жить на старом коде. Перезагружать её в момент активации нельзя:
 * человек может печатать ответ клиенту или заполнять шторку записи, а идущий запрос («Записать») оборвётся. Поэтому:
 * - смена управляющего service worker после первой установки (`controllerchange`) только ставит флаг «новая версия»;
 * - сразу перезагружаем только скрытую вкладку (`visibilityState === "hidden"`) без идущих запросов и несохранённых правок;
 * - иначе показываем плашку «Доступна новая версия» (`UpdateBanner`) и перезагружаем страницу на следующем переходе
 *   на другой путь (`noteLocation` из корневого маршрута, после размонтирования старой страницы), если нет идущих
 *   запросов и несохранённых правок; смена только параметров адреса переходом не считается;
 * - упавшая подгрузка чанка (`vite:preloadError`) перезагружает страницу сразу, не чаще раза в 10 секунд.
 * Несохранённые правки сообщают защиты форм (`useBeforeUnloadGuard` через `setUnsaved`); ленивых маршрутов в кабинете
 * нет (только `import("qrcode")`), так что перезагрузка по новой версии прежде всего держит код вкладки свежим.
 */

const unsaved = new Set<string>();
let updateReady = false;
let dismissed = false;
const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

/** Окружение: подменяется в тестах. */
let env = {
  reload: () => window.location.reload(),
  hidden: () => typeof document !== "undefined" && document.visibilityState === "hidden",
  mutating: () => queryClient.isMutating() > 0,
  typing: () => {
    if (typeof document === "undefined") return false;
    const el = document.activeElement;
    return (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement ||
      (el instanceof HTMLElement && el.isContentEditable)
    );
  },
};

/** Источник правок (страница или карточка) сообщает, есть ли у него несохранённое; `id` различает источники. */
export function setUnsaved(id: string, dirty: boolean): void {
  if (dirty) unsaved.add(id);
  else unsaved.delete(id);
}

export const hasUnsaved = (): boolean => unsaved.size > 0;

/** Страница занята: правки, идущий запрос или фокус в поле ввода. Такую автоматически не перезагружаем. */
export const busy = (): boolean => hasUnsaved() || env.mutating() || env.typing();

/** Новая версия активирована: скрытую и незанятую вкладку перезагружаем сразу, остальным плашка и перезагрузка на переходе. */
export function onNewVersion(): void {
  if (updateReady) return;
  updateReady = true;
  if (env.hidden() && !busy()) {
    reloadNow();
    return;
  }
  notify();
}

let lastPathname: string | null = null;

/**
 * Текущий путь после отрисовки (корневой маршрут, эффект по `pathname`). Смена пути при ожидающей версии даёт полную
 * загрузку новой версии; смена только `search` или `hash` (поиск клиентов, выбранный день, открытая запись) не даёт.
 * Пропущенный из-за правок или запроса переход возвращает плашку, если её спрятали.
 */
export function noteLocation(pathname: string): void {
  const changed = lastPathname !== null && lastPathname !== pathname;
  lastPathname = pathname;
  if (!changed || !updateReady || reloading) return;
  if (hasUnsaved() || env.mutating()) {
    if (dismissed) {
      dismissed = false;
      notify();
    }
    return;
  }
  reloadNow();
}

let reloading = false;
/** Автоматическая перезагрузка: один раз; если страница почему-то жива через секунду, попытку можно повторить. */
export function reloadNow(): void {
  if (reloading) return;
  reloading = true;
  setTimeout(() => {
    reloading = false;
  }, 1000);
  env.reload();
}

/** Кнопка «Обновить» на плашке: мимо флага автоматики (после «Остаться» в диалоге браузера кнопка должна работать снова). */
export function reloadByUser(): void {
  env.reload();
}

/** Спрятать плашку; перезагрузка на следующем переходе всё равно произойдёт, а пропущенный переход вернёт плашку. */
export function dismissBanner(): void {
  dismissed = true;
  notify();
}

/** Показывать ли плашку «Доступна новая версия» (снимок для `useUpdateBanner` и тестов). */
export const bannerShown = (): boolean => updateReady && !dismissed;

export function useUpdateBanner(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    bannerShown,
    () => false,
  );
}

/** Для тестов: подменить окружение и сбросить состояние. */
export function resetUpdateForTests(fake: Partial<typeof env>): void {
  unsaved.clear();
  updateReady = false;
  dismissed = false;
  reloading = false;
  lastPathname = null;
  env = { ...env, ...fake };
  notify();
}

const PRELOAD_RELOAD_KEY = "preload-reload-at";
const PRELOAD_RELOAD_MIN_MS = 10_000;

/**
 * Регистрация service worker и подписка на события обновления: вызывается один раз при старте приложения.
 * Регистрируем сами, а не автовставкой плагина: `virtual:pwa-register` тянет `workbox-window`, которого в зависимостях
 * нет, а нужные нам события браузер даёт и так. Регистрация после `load`, чтобы не отнимать сеть у первой отрисовки.
 * Проверку обновления просим при возврате во вкладку и раз в час: PWA у мастера открыт весь день.
 */
export function installUpdateHandlers(): void {
  // Vite не смог подгрузить ленивый чанк (после выкладки старых файлов на сервере нет): перезагрузка берёт новый index.
  // Защита от цикла: если перезагрузка не помогла, вторую раньше чем через 10 секунд не делаем.
  window.addEventListener("vite:preloadError", (event) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0);
    } catch {
      last = 0;
    }
    if (Date.now() - last < PRELOAD_RELOAD_MIN_MS) return;
    event.preventDefault();
    try {
      sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
    } catch {
      // приватный режим, не страшно
    }
    reloadNow();
  });
  if (!("serviceWorker" in navigator)) return;
  // Первая смена управляющего при пустом `controller` на старте это не новая версия: либо первая установка (clientsClaim),
  // либо страница после Shift+Reload, которая и так свежая. Редкий обратный случай (страница после Shift+Reload, а новая
  // версия выложена за минуту до её открытия) даст одну пропущенную перезагрузку; следующая выкладка поймается как обычно.
  let controlled = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (controlled) onNewVersion();
    controlled = true;
  });
  const register = () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((registration) => {
        const check = () => void registration.update().catch(() => {});
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
        setInterval(check, 60 * 60 * 1000);
      })
      .catch(() => {
        // В dev service worker не собирается; офлайн без него просто нет.
      });
  };
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
}
